import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import { ArrowLeft, PlayCircle } from "lucide-react";
import { Paper, Typography, Box, Button as MuiButton } from "@mui/material";
import { useThemeContext } from "./ThemeWrapper";

import Sidebar from "./Sidebar";
import RightPanel from "./RightPanel";

// --- Video Data ---
const videos = [
    {
        id: 1,
        title: "Introduction",
        subtitle: "紹介",
        image: "https://i.ytimg.com/vi/rGrBHiuPlT0/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLBm5iuaypmgzczFxOZsQwcjaahytA",
        youtubeLink: "https://www.youtube.com/watch?v=p9PEIsOzJ5E",
    },
    {
        id: 2,
        title: "How to count in Japanese",
        subtitle: "日本語で数える方法",
        image: "https://i.ytimg.com/vi/bOUqVC4XkOY/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLD2ScP2XqnZdEcJaT7LrbsCEcNiAA",
        youtubeLink: "https://www.youtube.com/watch?v=bOUqVC4XkOY",
    },
    {
        id: 3,
        title: "Days of the week and Days of the month",
        subtitle: "曜日と月の日",
        image: "https://i.ytimg.com/vi/JnoZE51WZg4/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLDAtGcTkvMcGSwQ9XXoZbTIWjJ1wA",
        youtubeLink: "https://www.youtube.com/watch?v=JnoZE51WZg4",
    },
    {
        id: 4,
        title: "Going to a Destination",
        subtitle: "目的地に行く",
        image: "https://i.ytimg.com/vi/k74yjmfFb_A/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLCAeB7K4-TbHslAXKK-NIY4dNQjew",
        youtubeLink: "https://www.youtube.com/watch?v=k74yjmfFb_A",
    },
    {
        id: 5,
        title: "Verbs",
        subtitle: "動詞",
        image: "https://i.ytimg.com/vi/KUIWRsVZZZA/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLBmjbuwwPh8hd2zHjBCHfnte_X9ZA",
        youtubeLink: "https://www.youtube.com/watch?v=KUIWRsVZZZA",
    },
    {
        id: 6,
        title: "To do (verb)",
        subtitle: "する（動詞）",
        image: "https://i.ytimg.com/vi/ZGGufccTLso/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLDncaSC6yLFnPdtpaZD5Spqv-B1-Q",
        youtubeLink: "https://www.youtube.com/watch?v=ZGGufccTLso",
    },
    {
        id: 7,
        title: "To give and to receive",
        subtitle: "あげる・もらう",
        image: "https://i.ytimg.com/vi/W0n-ODPwtzA/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLCnJ_6Nl8B1GRLAW4e3Zf1MuU0R2A",
        youtubeLink: "https://www.youtube.com/watch?v=W0n-ODPwtzA",
    },
    {
        id: 8,
        title: "Family Members",
        subtitle: "家族のメンバー",
        image: "https://i.ytimg.com/vi/p9PEIsOzJ5E/hqdefault.jpg?sqp=-oaymwEmCKgBEF5IWvKriqkDGQgBFQAAiEIYAdgBAeIBCggYEAIYBjgBQAE=&rs=AOn4CLCHiSVY-f5NHZmjw5DgqV4J6caq1g",
        youtubeLink: "https://www.youtube.com/watch?v=p9PEIsOzJ5E",
    },
];

// Framer Motion variants
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 50, scale: 0.8 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 100, damping: 10 },
  },
};

export default function Videos() {
  const navigate = useNavigate();
  const { isDarkTheme, bgPrimary, bgSecondary, textPrimary, textSecondary, borderColor } = useThemeContext();

  return (
    <div
      className="flex min-h-screen font-inter"
      style={{
        backgroundColor: bgPrimary,
        color: textPrimary,
      }}
    >
      {/* Sidebar */}
      <Sidebar />

      {/* Main content */}
      <main
        className="flex-1 p-4 sm:p-8 pb-20 md:p-12 overflow-y-auto"
        style={{ backgroundColor: bgPrimary, color: textPrimary }}
      >
        {/* Mobile back button */}
        <motion.div
          className="md:hidden absolute top-4 left-4 sm:top-8 sm:left-8 z-10"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 100, damping: 10 }}
        >
          <MuiButton
            variant="contained"
            startIcon={<ArrowLeft size={20} />}
            onClick={() => navigate("/hiragana")}
            className="!bg-orange-500 !text-white !font-bold !py-2 !px-4 !rounded-xl !shadow-md hover:!bg-orange-600"
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back
          </MuiButton>
        </motion.div>

        {/* Header */}
        <Box className="flex items-center mb-6 mt-16 md:mt-0 ml-4 md:ml-0">
          <Box
            sx={{
              display: "inline-block",
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#FF7F3F",
              mr: 1.5,
            }}
          />
          <Typography
            variant="h5"
            component="h2"
            className="!font-bold"
            style={{ color: textPrimary }}
          >
            Videos
          </Typography>
        </Box>

        {/* Videos Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-4 sm:p-0"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {videos.map((video) => (
            <motion.div
              key={video.id}
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                boxShadow: isDarkTheme
                  ? "0 8px 24px rgba(0,0,0,0.5)"
                  : "0 8px 24px rgba(0,0,0,0.15)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              <Paper
                elevation={3}
                className="rounded-xl overflow-hidden cursor-pointer h-full flex flex-col"
                style={{
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                }}
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <a
                    href={video.youtubeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                  >
                    <img
                      src={video.image}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                    />
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity duration-300"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                    >
                      <PlayCircle size={60} className="text-white opacity-80" />
                    </motion.div>
                  </a>
                </div>
                <div className="p-4 flex flex-col flex-grow">
                  <div className="flex items-center mb-2">
                    <span
                      className="font-bold text-sm rounded-md px-2 py-1 mr-2"
                      style={{
                        backgroundColor: isDarkTheme
                          ? "rgba(34,197,94,0.2)"
                          : "#dcfce7",
                        color: isDarkTheme ? "#bbf7d0" : "#166534",
                      }}
                    >
                      #{video.id}
                    </span>
                    <Typography
                      variant="h6"
                      component="h3"
                      className="!font-bold leading-tight"
                      style={{ color: textPrimary }}
                    >
                      {video.title}
                    </Typography>
                  </div>
                  <Typography
                    variant="body2"
                    style={{ color: textSecondary }}
                  >
                    {video.subtitle}
                  </Typography>
                </div>
              </Paper>
            </motion.div>
          ))}
        </motion.div>
      </main>

      {/* Right panel (desktop only) */}
      <div className="hidden lg:block w-80 border-l" style={{ borderColor }}>
        <RightPanel />
      </div>
    </div>
  );
}